import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessType } from '@/contexts/BusinessTypeContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OrderStatusBadge, formatCurrency, formatDateTime } from '@/lib/utils-helpers';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, ChefHat, Package, ChevronLeft, ChevronRight } from 'lucide-react';

export default function VendorOrders() {
  const navigate = useNavigate();
  const { config } = useBusinessType();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tab, setTab] = useState('all');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (tab !== 'all') params.status = tab;
      const res = await api.get('/vendor/orders', { params });
      setOrders(res.data.orders || []);
      setTotalPages(res.data.pages || 1);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const performAction = async (orderId, action, label) => {
    try {
      await api.put(`/vendor/orders/${orderId}/${action}`);
      toast.success(`Order ${label}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${label}`);
    }
  };

  const getActions = (order) => {
    const actions = [];
    if (order.status === 'placed') {
      actions.push({ label: 'Accept', action: 'accept', icon: CheckCircle2, color: 'text-emerald-600' });
      actions.push({ label: 'Decline', action: 'decline', icon: XCircle, color: 'text-rose-600' });
    }
    if (order.status === 'confirmed') {
      actions.push({ label: 'Prepare', action: 'prepare', icon: ChefHat, color: 'text-amber-600' });
    }
    if (order.status === 'preparing') {
      actions.push({ label: 'Ready', action: 'ready', icon: Package, color: 'text-teal-600' });
    }
    return actions;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">{config.ordersLabel} Management</h1>
        <p className="text-muted-foreground mt-1">Manage incoming orders through the fulfillment workflow.</p>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setPage(1); }}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="all" data-testid="vendor-orders-tab-all">All</TabsTrigger>
          <TabsTrigger value="placed" data-testid="vendor-orders-tab-placed">New</TabsTrigger>
          <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="preparing">Preparing</TabsTrigger>
          <TabsTrigger value="ready">Ready</TabsTrigger>
          <TabsTrigger value="delivered">Delivered</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}</div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => {
                const actions = getActions(order);
                const customerName = order.user_name || order.customer_name || 'Customer';
                return (
                  <div 
                    key={order.id} 
                    className="p-4 bg-muted/30 rounded-xl border cursor-pointer hover:shadow-md transition-shadow" 
                    data-testid={`vendor-order-${order.id}`}
                    onClick={() => navigate(`/vendor/orders/${order.id}`)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{customerName}</span>
                          <OrderStatusBadge status={order.status} />
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{(order.items || []).length} items</span>
                          <span className="font-medium tabular-nums text-foreground">{formatCurrency(order.total)}</span>
                          <span>{formatDateTime(order.created_at)}</span>
                        </div>
                        {order.items && order.items.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {order.items.map(i => `${i.name} x${i.quantity}`).join(', ')}
                          </p>
                        )}
                      </div>
                      {actions.length > 0 && (
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {actions.map(act => {
                            const Icon = act.icon;
                            return (
                              <Button
                                key={act.action}
                                variant="outline"
                                size="sm"
                                className={`${act.color} border-current/20`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  performAction(order.id, act.action, act.label);
                                }}
                                data-testid={`vendor-order-${act.action}-${order.id}`}
                              >
                                <Icon className="h-3.5 w-3.5 mr-1" /> {act.label}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {orders.length === 0 && <p className="text-center py-8 text-muted-foreground">No orders found</p>}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
